// src/lib/layouts/enforcedSubtitlePosition.ts

export type SubtitleAlignItems = 'flex-start' | 'center' | 'flex-end';

const ALIGN_ITEMS_VALUES: SubtitleAlignItems[] = ['flex-start', 'center', 'flex-end'];

/** Campaign subtitle position: brand-kit-style anchor + margin relative to it. */
export interface EffectiveSubtitlePosition {
  alignItems: SubtitleAlignItems;
  verticalMargin: number;
  locked: boolean;
}

/**
 * Parse the `effective_subtitle_position` JSON blob from the API.
 * Mirrors the backend resolver normalization: usable when it carries a valid
 * anchor or a numeric margin; missing keys default to flex-end / 25.
 */
export function parseEffectiveSubtitlePosition(blob: unknown): EffectiveSubtitlePosition | null {
  if (!blob || typeof blob !== 'object' || Array.isArray(blob)) return null;
  const b = blob as Record<string, unknown>;
  const validAlign = ALIGN_ITEMS_VALUES.includes(b.align_items as SubtitleAlignItems);
  const validMargin =
    typeof b.vertical_margin_percentage === 'number' && !Number.isNaN(b.vertical_margin_percentage);
  if (!validAlign && !validMargin) return null;
  return {
    alignItems: validAlign ? (b.align_items as SubtitleAlignItems) : 'flex-end',
    verticalMargin: validMargin ? (b.vertical_margin_percentage as number) : 25,
    locked: b.locked === true,
  };
}

export interface EnforcedValue<T> {
  value: T;
  locked: boolean;
}

/**
 * Effective value for one enforceable subtitle setting.
 * Precedence: locked campaign value > user edit > campaign suggestion >
 * brand kit > default. `userValue: null` means the user hasn't touched it.
 */
export function resolveEnforcedValue<T>({
  enforced,
  brandKitValue,
  userValue,
  defaultValue,
}: {
  enforced: EnforcedValue<T> | null;
  brandKitValue: T | null | undefined;
  userValue: T | null;
  defaultValue: T;
}): T {
  if (enforced?.locked) return enforced.value;
  if (userValue != null) return userValue;
  if (enforced) return enforced.value;
  if (brandKitValue != null) return brandKitValue;
  return defaultValue;
}
